import type { flows } from '@repletoryreach/sdk-api';
import type { Translate } from '../../../i18n/I18nProvider';

// Loose ends (2 November 2026): a step in a switched-on flow that leads nowhere, or a flow that never starts. SJM built
// improved copies of its flows, deleted the originals by hand and switched the copies on; their Run another flow steps
// still opened the deleted flows, so menu buttons ended the chat in silence and nobody was told. The rules behind the
// list, kept free of React so they are tested on their own.

/** Under `['flows']`, so anything that invalidates the flows list checks the loose ends again too. */
export const LOOSE_ENDS_KEY = ['flows', 'loose-ends'] as const;

export interface LooseEndGroup {
  flowId: string;
  flowName: string;
  items: flows.LooseEnd[];
  /** How many of them stop the chat. */
  breaks: number;
}

/** The loose ends grouped by the flow they are in, in the order the service lists the flows; within a flow, the ones that stop the chat first. */
export function groupByFlow(items: readonly flows.LooseEnd[]): LooseEndGroup[] {
  const groups = new Map<string, LooseEndGroup>();
  for (const e of items) {
    const g = groups.get(e.flow_id) ?? { flowId: e.flow_id, flowName: e.flow_name, items: [], breaks: 0 };
    g.items.push(e);
    if (e.breaks) g.breaks += 1;
    groups.set(e.flow_id, g);
  }
  return [...groups.values()].map((g) => ({ ...g, items: [...g.items.filter((e) => e.breaks), ...g.items.filter((e) => !e.breaks)] }));
}

/** The step a loose end is on, as the list names it on its own line: the whole flow when two flows start the same way. */
export function looseEndStep(t: Translate, e: flows.LooseEnd): string {
  if (e.kind === 'same_start' || !e.step_id) return t('flows.looseEnds.wholeFlow');
  return e.step_name?.trim() || t('flows.looseEnds.unnamedStep');
}

/** What is wrong, in the reader's words. The step is named on the line above, so the sentence does not repeat it. */
export function looseEndText(t: Translate, e: flows.LooseEnd): string {
  const target = e.target_name?.trim() || t('flows.looseEnds.anotherFlow');
  switch (e.kind) {
    case 'opens_missing': return e.target ? t('flows.looseEnds.kind.opens_missing') : t('flows.looseEnds.kind.opens_none');
    case 'opens_off': return t('flows.looseEnds.kind.opens_off', { target });
    case 'opens_old_version': return t('flows.looseEnds.kind.opens_old_version', { target });
    case 'same_start': return t('flows.looseEnds.kind.same_start', { target });
    case 'button_nowhere': return t('flows.looseEnds.kind.button_nowhere', { button: e.target_name?.trim() || e.target });
    case 'key_missing': return t('flows.looseEnds.kind.key_missing');
    default: return t('flows.looseEnds.kind.other');
  }
}

/** The Fix button's words: "Point it at Welcome" or "Switch Welcome off"; null when the service has no fix to offer. */
export function fixLabel(t: Translate, fix: flows.LooseEndFix | null): string | null {
  if (!fix) return null;
  const name = fix.to_name?.trim() || t('flows.looseEnds.anotherFlow');
  return fix.action === 'switch_off' ? t('flows.looseEnds.fix.switchOff', { name }) : t('flows.looseEnds.fix.relink', { name });
}

/** Where Open the step goes: the builder with the step selected (`?step=`), or the flow itself when it is the whole flow. */
export function stepHref(e: flows.LooseEnd): string {
  const base = `/flows/${encodeURIComponent(e.flow_id)}/builder`;
  return e.step_id && e.kind !== 'same_start' ? `${base}?step=${encodeURIComponent(e.step_id)}` : base;
}

/** "3 loose ends in your flows." and, when some stop the chat, how many. */
export function looseEndsSummary(t: Translate, ends: Pick<flows.LooseEnds, 'items'>): string {
  const count = ends.items.length;
  const breaking = ends.items.filter((e) => e.breaks).length;
  const head = count === 1 ? t('flows.looseEnds.barOne') : t('flows.looseEnds.bar', { count });
  return breaking > 0 ? `${head} ${t('flows.looseEnds.barBreaking', { count: breaking })}` : head;
}
